import { INSPECTION_STATUSES } from '../_constants/inspectionConst';
import {
  INSPEX_PATHNAME,
  SESSION_STORAGE_KEY,
  VALID_YEAR_RANGE,
} from '../_constants/Const';
import { Attribute, GetRepairRequest, GetUserInfo } from '../services/apiTypes';
import { Dayjs } from 'dayjs';
import { USER_ROLE } from '../_constants/manageConst';

/** ユーザーがメ内サか */
export function isAdmin(userInfo: GetUserInfo) {
  return userInfo.roleId === USER_ROLE.ADMIN;
}

/** ユーザーが地域管理者か */
export function isAreaManager(userInfo: GetUserInfo) {
  return userInfo.roleId === USER_ROLE.AREA_MANAGER;
}

/** ユーザーが一般か */
export function isNormalUser(userInfo: GetUserInfo) {
  return userInfo.roleId === USER_ROLE.NORMAL_USER;
}

/** ユーザーがメ内サまたは地域管理者か */
export function isManagerOrAdmin(userInfo: GetUserInfo) {
  return (
    userInfo.roleId === USER_ROLE.ADMIN ||
    userInfo.roleId === USER_ROLE.AREA_MANAGER
  );
}

/** UTCタイムからローカルタイムに変換 */
function convertUTCToLocal(utcDate: Date): Date {
  const timeZone = utcDate.getTimezoneOffset() ?? 0;
  return new Date(utcDate.getTime() - timeZone * 60000);
}

/**
 * 日付のフォーマットを表示用に変換
 */
export function formatDate(
  date?: Date | string,
  format?: number,
  isDisplayTime = false,
) {
  if (isEmptyData(date)) {
    return '';
  }

  const utcDate = new Date(date);
  const localDate = convertUTCToLocal(utcDate);

  // 年、月、日を取得
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');

  let formattedDate: string;

  switch (format) {
    case 1: // MM-DD-YYYY
      formattedDate = `${month}-${day}-${year}`;
      break;
    case 2: // DD-MM-YY
      formattedDate = `${day}-${month}-` + year.toString().slice(-2);
      break;
    case 0:
    default: // YYYY-MM-DD
      formattedDate = `${year}-${month}-${day}`;
      break;
  }

  if (isDisplayTime) {
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const seconds = String(localDate.getSeconds()).padStart(2, '0');
    formattedDate += ` ${hours}:${minutes}:${seconds}`;
  }

  return formattedDate;
}

/**
 * 日付のフォーマットをAPIのクエリパラメータ用に変換
 */
export function formatQueryDateTime(dateString: string, isFrom: boolean) {
  // 表示形式に依存しない形で年月日を取得
  const date = new Date(dateString);
  const tempYear = date.getFullYear();
  const tempMonth = date.getMonth();
  const tempDay = isFrom ? date.getDate() : date.getDate() + 1;

  // ローカルタイムゾーンの午前0時
  const localDate = new Date(tempYear, tempMonth, tempDay);

  // UTC基準の年月日、時分秒を取得
  const year = localDate.getUTCFullYear();
  const month = localDate.getUTCMonth() + 1;
  const day = localDate.getUTCDate();
  const hours = localDate.getUTCHours();
  const minutes = localDate.getUTCMinutes();
  const seconds = localDate.getUTCSeconds();

  // 年以外の各要素を2桁にする
  const f = (n: number) => n.toString().padStart(2, '0');

  return `${year}-${f(month)}-${f(day)} ${f(hours)}:${f(minutes)}:${f(seconds)}`;
}

/** true:デフォルトテーマ(緑)　false:出荷検査用テーマ */
export function isDefaultTheme(
  pathname: string,
  repairRequest?: GetRepairRequest,
): boolean {
  // 修理依頼検索、管理系画面はデフォルトのテーマを適用
  if (pathname === INSPEX_PATHNAME.RMA_SEARCH || pathname.includes('Manage')) {
    return true;
  }
  // シリアルチェック画面ではQC_WAITのステータスで出荷検査用のテーマを適用
  if (
    repairRequest &&
    pathname === INSPEX_PATHNAME.SERIAL_CHECK &&
    repairRequest.status === INSPECTION_STATUSES.INSPECTION_COMPLETE
  ) {
    return false;
  }
  // 180: 受入診断完了
  return (
    !repairRequest ||
    repairRequest.status <= INSPECTION_STATUSES.INSPECTION_COMPLETE
  );
}

/** true:受入診断　false:出荷検査 */
export function isInspection(status: number): boolean {
  // 180: 受入診断完了
  return status <= INSPECTION_STATUSES.INSPECTION_COMPLETE;
}

/** セッションストレージのリダイレクト用URLを取得(設定されていなければ「"/"」を返却) */
export function getSessionStorageRedirectUrl() {
  const redirectUrl = sessionStorage.getItem(SESSION_STORAGE_KEY.REDIRECT_URL);
  if (redirectUrl === null) {
    return INSPEX_PATHNAME.RMA_SEARCH;
  } else {
    return redirectUrl;
  }
}

/** セッションストレージにリダイレクト用URLが設定されていない場合のみ設定 */
export function setSessionStorageRedirectUrl() {
  const redirectUrl = getSessionStorageRedirectUrl();

  if (redirectUrl === INSPEX_PATHNAME.RMA_SEARCH) {
    let currentUrl = window.location.hash.replace('#', '');
    if (currentUrl === '' || currentUrl === INSPEX_PATHNAME.LOGIN) {
      currentUrl = INSPEX_PATHNAME.RMA_SEARCH;
    }
    sessionStorage.setItem(SESSION_STORAGE_KEY.REDIRECT_URL, currentUrl);
  }
}

/** nullまたはundefinedかどうか */
export function isNullOrUndefined(data: any): data is null | undefined {
  return data === null || data === undefined;
}

/** nullまたはundefinedまたは空文字かどうか */
export function isEmptyData(data: any): data is null | undefined | '' {
  return isNullOrUndefined(data) || data.toString() === '';
}

/** nullとundefinedを同じものとして扱いながら、文字列または数値の値を比較 */
export function isEqualNullable(a: unknown, b: unknown): boolean {
  const normalize = (value: unknown) =>
    value === undefined || value === null ? null : value;
  return normalize(a) === normalize(b);
}

/** エクスポートするExcelのファイル名を作成 */
export function createExportExcelName(baseName: string, dateFormat: number) {
  // 現在時刻をUTC時刻で取得
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');

  const formattedUTC = `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;

  // 共通のフォーマットに整備
  const formattedString = formatDate(formattedUTC, dateFormat, true)
    .replaceAll('-', '') // 日時を連結
    .replaceAll(':', '') // 時刻を連結
    .replaceAll(' ', '_'); // 日時と時刻を'_'で連結

  return baseName.replaceAll(' ', '_') + '_' + formattedString + '.xlsx';
}

/** 診断関連の画面か(修理依頼に紐づいた画面か) */
export function isInspectionScreen(path: string) {
  return (
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.SERIAL_CHECK) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.INSPECTION_TOP) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.INSPECTION_INPUT_RESULT) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.CONFIRMED_INSPECTION) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.PARTS_SELECTION) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.REPORT) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.CONFIRMED_QC) ||
    // 診断画面にはidが含まれるため別途比較
    path
      .toLocaleLowerCase()
      .includes(INSPEX_PATHNAME.INSPECTION.toLocaleLowerCase())
  );
}

/** 管理関連の画面か */
export function isManageScreen(path: string) {
  return (
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_INSPECTION_ITEM) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_CRITERIA) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_TEMPLATE) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.REPORT_FORMAT) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_USER) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_ORGANIZATION) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_CODE_MASTER) ||
    compareStringsIgnoreCase(path, INSPEX_PATHNAME.MANAGE_HISTORY)
  );
}

/**
 * 入力された数値が有効桁数外の場合、最小(0)または最大の値に変換する
 * @param value 対象の値
 * @param maxDigits 桁数(1以上)
 */
export function adjustToMaxDigits(value: number, maxDigits: number): number {
  const factor = Math.pow(10, maxDigits);
  const maxValue = factor - 1;
  const adjustedValue = Math.round(value * factor) / factor;
  return Math.max(adjustedValue, 0) && Math.min(adjustedValue, maxValue);
}

/** 入力された年月日(dayjs)が有効範囲外の場合、最小または最大の年数に変換する */
export function adjustInputYear(value: Dayjs) {
  const year = Number(value.year());
  const diffValue =
    (year -
      (Math.max(year, VALID_YEAR_RANGE.MIN) !== VALID_YEAR_RANGE.MIN
        ? Math.min(year, VALID_YEAR_RANGE.MAX)
        : VALID_YEAR_RANGE.MIN)) *
    -1;
  value = value.add(diffValue, 'year');
  return value;
}

/** 大文字・小文字を区別せずに2つの文字列が同じかどうか比較 */
export function compareStringsIgnoreCase(str1: string, str2: string): boolean {
  return str1.toLowerCase() === str2.toLowerCase();
}

/** オブジェクトが空であることを確認 */
export function isEmptyObject(obj: Record<string, any>): boolean {
  return Object.values(obj).every((value) => isEmptyData(value));
}

/** 入力データを正しい型に変換 */
export function convertInputDataToCorrectType(
  data: any,
  correctType: string,
): any {
  let result = undefined;
  switch (correctType) {
    case 'number':
      if (typeof data === 'number') {
        result = data;
      } else if (typeof data === 'string') {
        if (data === '') {
          result = null;
        } else if (!Number.isNaN(Number(data))) {
          result = Number(data);
        }
      } else if (data === null || data === undefined) {
        result = null;
      }
      break;

    case 'string':
      if (typeof data === 'string') {
        result = data;
      } else if (data === null || data === undefined) {
        result = '';
      } else {
        result = data.toString();
      }
      break;

    case 'boolean':
      if (typeof data === 'boolean') {
        result = data;
      } else if (typeof data === 'string') {
        if (data.toUpperCase() === 'TRUE') {
          result = true;
        } else if (data.toUpperCase() === 'FALSE' || data === '') {
          result = false;
        }
      } else if (data === null || data === undefined) {
        result = false;
      }
      break;
    default:
      // 何もしない
      break;
  }

  return result;
}

/** 不明なキーがあるか確認 */
export function hasUndefinedMember(obj: any) {
  for (const key in obj) {
    if (obj[key] === undefined) {
      return true;
    }
  }
  return false;
}

/** ステータスに応じて画面を遷移させる */
export function getRedirectUrlByStatus(status: number): string {
  switch (status) {
    case INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK:
      return INSPEX_PATHNAME.SERIAL_CHECK;
    case INSPECTION_STATUSES.INSPECTION_IN_PROGRESS:
      return INSPEX_PATHNAME.INSPECTION_TOP;
    case INSPECTION_STATUSES.INSPECTION_INPUT_RESULT:
      return INSPEX_PATHNAME.INSPECTION_INPUT_RESULT;
    case INSPECTION_STATUSES.INSPECTION_CONFIRM:
      return INSPEX_PATHNAME.CONFIRMED_INSPECTION;
    case INSPECTION_STATUSES.INSPECTION_PARTS_SELECT:
      return INSPEX_PATHNAME.PARTS_SELECTION;
    case INSPECTION_STATUSES.INSPECTION_NOT_LINKED:
      return INSPEX_PATHNAME.REPORT;
    case INSPECTION_STATUSES.NO_FAULT_FOUND:
      return INSPEX_PATHNAME.REPORT;
    case INSPECTION_STATUSES.INSPECTION_COMPLETE:
      return INSPEX_PATHNAME.SERIAL_CHECK;
    case INSPECTION_STATUSES.QC_IN_PROGRESS:
      return INSPEX_PATHNAME.INSPECTION_TOP;
    case INSPECTION_STATUSES.QC_CONFIRM:
      return INSPEX_PATHNAME.CONFIRMED_QC;
    case INSPECTION_STATUSES.QC_REPAIRING:
      return INSPEX_PATHNAME.REPORT;
    case INSPECTION_STATUSES.QC_NOT_LINKED:
      return INSPEX_PATHNAME.REPORT;
    case INSPECTION_STATUSES.QC_COMPLETE:
      return INSPEX_PATHNAME.REPORT;
    default:
      return INSPEX_PATHNAME.RMA_SEARCH;
  }
}

/** 属性の親子を渡して自動入力の判定と属性を取得する */
export function getAutoInputAttributes(
  parent: Attribute[],
  child: Attribute[],
): { parent: Attribute[]; child: Attribute[] } {
  const updateAttributes = { parent, child };
  // 親に値がある
  if (parent.length > 0) {
    if (child.length > 0) {
      // 親に不足している属性があれば付与する
      child.map((childAttr) => {
        if (!parent.some((parentAttr) => parentAttr.id === childAttr.id)) {
          updateAttributes.parent.push(childAttr);
        }
      });
    } else {
      // 子が空なら親の属性を付与する
      updateAttributes.child = parent;
    }
  }
  return updateAttributes;
}

/**
 * stringの入力チェックをする
 * true: trimしても空ではない場合
 */
export function isStringInput(value: any): boolean {
  if (isEmptyData(value)) {
    return false;
  }
  // trim用に型チェックを行う
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  // 値があってもstring以外はfalse
  return false;
}
